// src/pages/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Paper, Divider, Chip } from '@mui/material';
import { keyframes } from '@mui/system';
import StatCard from '../components/StatCard';
import { getDashboardSummary } from '../services/dashboardService';
import { StatShimmer } from '../components/Shimmer';

import PeopleIcon from '@mui/icons-material/People';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ClassIcon from '@mui/icons-material/Class';
import SubjectIcon from '@mui/icons-material/Subject';
import TranslateIcon from '@mui/icons-material/Translate';
import UpdateIcon from '@mui/icons-material/Update';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const STAT_CONFIG: Record<string, { label: string; gradient: string; icon: React.ReactNode }> = {
    students: { label: 'Students', gradient: 'linear-gradient(135deg,#667eea,#764ba2)', icon: <PeopleIcon /> },
    videos: { label: 'Videos', gradient: 'linear-gradient(135deg,#f093fb,#f5576c)', icon: <VideoLibraryIcon /> },
    classes: { label: 'Classes', gradient: 'linear-gradient(135deg,#4facfe,#00f2fe)', icon: <ClassIcon /> },
    subjects: { label: 'Subjects', gradient: 'linear-gradient(135deg,#43e97b,#38f9d7)', icon: <SubjectIcon /> },
    mediums: { label: 'Mediums', gradient: 'linear-gradient(135deg,#fa709a,#fee140)', icon: <TranslateIcon /> },
    updated: { label: 'Updated', gradient: 'linear-gradient(135deg,#a18cd1,#fbc2eb)', icon: <UpdateIcon /> },
    created: { label: 'Created', gradient: 'linear-gradient(135deg,#ffecd2,#fcb69f)', icon: <AddCircleOutlineIcon /> },
    due: { label: 'Due', gradient: 'linear-gradient(135deg,#ff9a9e,#fad0c4)', icon: <EventBusyIcon /> },
};

const PREFERRED_ORDER = ['students', 'videos', 'classes', 'subjects', 'mediums', 'updated', 'created', 'due'];

type Stat = { id: string; label: string; value: number; gradient: string; icon: React.ReactNode };

function useLiveClock() {
    const [time, setTime] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    return time;
}

export default function DashboardPage(): React.ReactElement {
    const [stats, setStats] = useState<Stat[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const now = useLiveClock();

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getDashboardSummary();
                if (!mounted) return;

                const arr: Stat[] = Object.keys(data).map((k) => {
                    const cfg = STAT_CONFIG[k];
                    return {
                        id: k,
                        label: cfg?.label ?? k,
                        value: Number(data[k]) || 0,
                        gradient: cfg?.gradient ?? 'linear-gradient(135deg,#667eea,#764ba2)',
                        icon: cfg?.icon ?? <DashboardRoundedIcon />,
                    };
                });

                const ordered = PREFERRED_ORDER
                    .filter(k => arr.some(s => s.id === k))
                    .map(k => arr.find(s => s.id === k)!)
                    .concat(arr.filter(s => !PREFERRED_ORDER.includes(s.id)));

                setStats(ordered);
            } catch (err: any) {
                console.error('Dashboard load failed', err);
                setError(err?.message ?? 'Failed to load dashboard');
                setStats(null);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const greeting = () => {
        const h = now.getHours();
        if (h < 12) return 'Good morning';
        if (h < 17) return 'Good afternoon';
        return 'Good evening';
    };

    const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    return (
        <Box sx={{ minHeight: '100vh', background: '#f5f6fa', p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>

                {/* ── Hero Header ── */}
                <Paper
                    elevation={0}
                    sx={{
                        mb: 4,
                        borderRadius: 4,
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #174143 0%, #2a6b6f 50%, #3d9ea4 100%)',
                        color: '#fff',
                        animation: `${fadeIn} 0.5s ease-out`,
                        position: 'relative',
                    }}
                >
                    {/* decorative circles */}
                    <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', bottom: -30, right: 80, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />

                    <Box sx={{ p: { xs: 3, md: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, position: 'relative' }}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                <DashboardRoundedIcon sx={{ fontSize: 28, opacity: 0.85 }} />
                                <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.75, fontWeight: 600, fontSize: '0.72rem' }}>
                                    Admin Dashboard
                                </Typography>
                            </Box>
                            <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1, letterSpacing: -0.5 }}>
                                {greeting()}, Admin 👋
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.8, opacity: 0.75 }}>
                                Here's what's happening with Vidya Samruddhi today.
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, mb: 0.5 }}>
                                <CalendarTodayRoundedIcon sx={{ fontSize: 15, opacity: 0.7 }} />
                                <Typography variant="caption" sx={{ opacity: 0.75 }}>{dateStr}</Typography>
                            </Box>
                            <Typography
                                variant="h5"
                                fontWeight={700}
                                sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    letterSpacing: 1,
                                    opacity: 0.95,
                                    fontFamily: '"Inter", monospace',
                                }}
                            >
                                {timeStr}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* ── Section label ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#174143' }}>
                        Overview
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                    {!loading && stats && (
                        <Chip label={`${stats.length} metrics`} size="small" sx={{ background: '#174143', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                </Box>

                {/* ── Stat Grid ── */}
                {loading ? (
                    <StatShimmer count={6} />
                ) : error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <Typography color="error">⚠ {error}</Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2.5,
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                                lg: 'repeat(4, 1fr)',
                            },
                        }}
                    >
                        {stats && stats.map((stat, i) => (
                            <StatCard
                                key={stat.id}
                                title={stat.label}
                                value={stat.value}
                                icon={stat.icon}
                                gradient={stat.gradient}
                                delay={i * 60}
                            />
                        ))}
                    </Box>
                )}

                {/* ── Footer note ── */}
                <Box sx={{ mt: 5, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.disabled">
                        Vidya Samruddhi Admin · Data refreshes on page load
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}